import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown } from "lucide-react";

import Button from "../Button/Button";
import Container from "../Container/Container";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import { useScrollPosition } from "../../Hooks/useScrollPosition";
import { cx, scrollToSection } from "../../Utils/helpers";
import logo from "../../Assets/logo/connectify-logo.png";

import "../../Style/navbar.css";

const NAV_ITEMS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  {
    label: "Services",
    href: "/#services",
    dropdown: [
      { label: "B2B Contact Insights", href: "/services/b2b-contact-insights" },
      { label: "B2B Company Data", href: "/services/b2b-company-data" },
      { label: "Data Appending", href: "/services/data-appending" },
      { label: "Data Validation", href: "/services/data-validation" },
      { label: "Data Verification", href: "/services/data-verification" },
      { label: "B2B Appointment", href: "/services/b2b-appointment" },
      { label: "Email Marketing", href: "/services/email-marketing" },
      { label: "Lead Generation", href: "/services/lead-generation" },
      { label: "Company Profiling", href: "/services/company-profiling" },
    ],
  },
  {
    label: "Hire Us",
    href: "/services/recruitment",
    dropdown: [
      { label: "Recruitment & Staffing", href: "/services/recruitment" },
      { label: "Hire Developers", href: "/hire-talent" },
    ],
  },
  {
    label: "Resources",
    href: "/#knowledge-hub",
    dropdown: [
      { label: "Data Privacy", href: "/privacy" },
      { label: "Data Accuracy", href: "/data-accuracy" },
      { label: "Data Coverage", href: "/data-coverage" },
      { label: "Our Blog", href: "/blog" },
      { label: "Career", href: "/career" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  { label: "Contact", href: "/contact" },
];

const LANGUAGES = [
  {
    code: "en",
    label: "EN",
    fullName: "English",
    flag: "https://flagcdn.com/w40/us.png",
  },
  {
    code: "fr",
    label: "FR",
    fullName: "Français",
    flag: "https://flagcdn.com/w40/fr.png",
  },
];

/* Custom Glassmorphism Language Dropdown Component */
function LanguageMenu({ currentLang, onSelectLanguage, isMobile = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const active = LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0];

  return (
    <div className={cx("nav-lang", "notranslate", isMobile && "nav-lang--mobile")} ref={menuRef}>
      <button
        type="button"
        className={cx("nav-lang__trigger", isOpen && "is-open")}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <img
          src={active.flag}
          alt={active.label}
          className="nav-lang__flag"
          width="18"
          height="13"
        />
        <span className="nav-lang__label">{isMobile ? active.fullName : active.label}</span>
        <ChevronDown
          size={13}
          className={cx("nav-lang__arrow", isOpen && "is-rotated")}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="nav-lang__menu"
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
          >
            {LANGUAGES.map((lang) => {
              const isSelected = lang.code === currentLang;
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={cx("nav-lang__item", isSelected && "is-selected")}
                  onClick={() => {
                    setIsOpen(false);
                    onSelectLanguage(lang.code);
                  }}
                >
                  <img
                    src={lang.flag}
                    alt={lang.label}
                    className="nav-lang__flag"
                    width="18"
                    height="13"
                  />
                  <span className="nav-lang__item-name">{lang.fullName}</span>
                  {isSelected && <Check size={14} className="nav-lang__check" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const dropdownVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.96, pointerEvents: "none" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    pointerEvents: "auto",
    transition: { type: "spring", stiffness: 420, damping: 28, mass: 0.8 },
  },
  exit: {
    opacity: 0,
    y: 6,
    scale: 0.97,
    pointerEvents: "none",
    transition: { duration: 0.18, ease: "easeOut" },
  },
};

const mobileMenuVariants = {
  hidden: { opacity: 0, height: 0 },
  show: { opacity: 1, height: "auto", transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } },
};

function Navbar({ onMenuToggle, currentPath: propPath = "/" }) {
  const { isScrolled } = useScrollPosition(40);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const [currentLang, setCurrentLang] = useState("en");

  // Keep internal path synced with window.location and popstate events
  const [locationPath, setLocationPath] = useState(
    typeof window !== "undefined" ? window.location.pathname : propPath
  );

  useEffect(() => {
    setLocationPath(propPath);
  }, [propPath]);

  useEffect(() => {
    const handlePopState = () => {
      setLocationPath(window.location.pathname);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const match = document.cookie.match(/(^|;) ?googtrans=([^;]*)(;|$)/);
    if (match) {
      const code = match[2].split("/").pop();
      if (code) setCurrentLang(code);
    }
  }, []);

  const handleLanguageChange = (langCode) => {
    if (langCode === currentLang) return;
    setCurrentLang(langCode);

    if (langCode === "en") {
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    } else {
      const cookieVal = `/en/${langCode}`;
      document.cookie = `googtrans=${cookieVal}; path=/;`;
    }

    window.location.reload();
  };

  useLockBodyScroll(isOpen);

  const setMenuState = (nextState) => {
    const resolvedState = typeof nextState === "function" ? nextState(isOpen) : nextState;
    setIsOpen(resolvedState);
    if (onMenuToggle) {
      onMenuToggle(resolvedState);
    }
  };

  // Nav click with mobile drawer close & instant top scroll reset
  const handleNavClick = (href) => (event) => {
    if (event) event.preventDefault();
    setMenuState(false);
    setActiveDropdown(null);

    // Release scroll lock immediately
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";

    const isCurrentPageHome = locationPath === "/" || locationPath === "";

    // 1. Hash scrolls on home page
    if (href.startsWith("#") || (href.startsWith("/#") && isCurrentPageHome)) {
      const targetHash = href.replace("/", "");
      scrollToSection(targetHash);
      return;
    }

    // 2. Hash link from another page: go to home then scroll
    if (href.startsWith("/#") && !isCurrentPageHome) {
      window.history.pushState({}, "", "/");
      window.dispatchEvent(new Event("popstate"));
      setTimeout(() => {
        const targetHash = href.replace("/", "");
        scrollToSection(targetHash);
      }, 100);
      return;
    }

    // 3. Normal route changes: pushState & force top scroll
    if (href.startsWith("/")) {
      if (locationPath !== href) {
        window.history.pushState({}, "", href);
        window.dispatchEvent(new Event("popstate"));
      }

      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      });
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }, 50);
    }
  };

  const toggleMobileSubmenu = (label) => {
    setMobileExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Route matching with support for aliases
  const isPathActive = (targetHref) => {
    if (!targetHref) return false;
    if (targetHref === "/") {
      return locationPath === "/" || locationPath === "";
    }
    if (
      (targetHref === "/services/recruitment" || targetHref === "/recruitment") &&
      (locationPath === "/services/recruitment" || locationPath === "/recruitment")
    ) {
      return true;
    }
    if (
      (targetHref === "/hire-talent" || targetHref === "/hiring") &&
      (locationPath === "/hire-talent" || locationPath === "/hiring")
    ) {
      return true;
    }
    if (
      (targetHref === "/privacy" || targetHref === "/data-privacy") &&
      (locationPath === "/privacy" || locationPath === "/data-privacy")
    ) {
      return true;
    }
    return locationPath === targetHref;
  };

  // Check if item or any of its subitems is active
  const isItemActive = (item) => {
    if (item.dropdown && Array.isArray(item.dropdown)) {
      return item.dropdown.some((subItem) => isPathActive(subItem.href));
    }
    return isPathActive(item.href);
  };

  return (
    <header className={cx("navbar", isScrolled && "navbar--scrolled", isOpen && "navbar--open")}>
      <Container className="navbar__inner">
        <a href="/" className="navbar__brand" onClick={handleNavClick("/")}>
          <motion.img
            src={logo}
            alt="Conectify Tech"
            className="navbar__logo"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="navbar__links" aria-label="Primary">
          {NAV_ITEMS.map((item) => {
            const hasDropdown = Boolean(item.dropdown);
            const isDropdownOpen = activeDropdown === item.label;
            const active = isItemActive(item);

            return (
              <div
                key={item.label}
                className="navbar__item-wrapper"
                onMouseEnter={() => hasDropdown && setActiveDropdown(item.label)}
                onMouseLeave={() => hasDropdown && setActiveDropdown(null)}
              >
                {hasDropdown ? (
                  <button
                    type="button"
                    className={cx(
                      "navbar__link",
                      "navbar__link--trigger",
                      active && "navbar__link--active",
                      isDropdownOpen && "is-open"
                    )}
                    onClick={handleNavClick(item.href)}
                    aria-expanded={isDropdownOpen}
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      size={14}
                      className={cx("navbar__chevron", isDropdownOpen && "navbar__chevron--rotated")}
                    />
                  </button>
                ) : (
                  <a
                    href={item.href}
                    className={cx("navbar__link", active && "navbar__link--active")}
                    onClick={handleNavClick(item.href)}
                  >
                    {item.label}
                  </a>
                )}

                {/* Desktop Dropdown Menu */}
                {hasDropdown && (
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        className="navbar__dropdown"
                        variants={dropdownVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <div className="navbar__dropdown-menu">
                          {item.dropdown.map((subItem) => {
                            const isSubActive = isPathActive(subItem.href);
                            return (
                              <a
                                key={subItem.label}
                                href={subItem.href}
                                className={cx(
                                  "navbar__dropdown-item",
                                  isSubActive && "navbar__dropdown-item--active"
                                )}
                                onClick={handleNavClick(subItem.href)}
                              >
                                {subItem.label}
                              </a>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </nav>

        {/* Action Buttons */}
        <div className="navbar__actions">
          {/* Desktop Flag Dropdown */}
          <div className="nav-lang--desktop">
            <LanguageMenu
              currentLang={currentLang}
              onSelectLanguage={handleLanguageChange}
            />
          </div>

          <ThemeToggle className="navbar__theme-toggle" />

          <Button
            size="sm"
            className="navbar__cta navbar__cta--desktop"
            icon={<ArrowRight size={16} />}
            onClick={handleNavClick("/contact")}
          >
            Get Started
          </Button>

          <button
            type="button"
            className="navbar__burger"
            onClick={() => setMenuState((open) => !open)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            <motion.span animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.25 }} />
            <motion.span animate={isOpen ? { opacity: 0 } : { opacity: 1 }} transition={{ duration: 0.18 }} />
            <motion.span animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.25 }} />
          </button>
        </div>
      </Container>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="navbar__mobile"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <nav className="navbar__mobile-links" aria-label="Mobile">
              {NAV_ITEMS.map((item) => {
                const hasDropdown = Boolean(item.dropdown);
                const isExpanded = mobileExpanded[item.label];
                const active = isItemActive(item);

                return (
                  <div key={item.label} className="navbar__mobile-item">
                    {hasDropdown ? (
                      <>
                        <button
                          type="button"
                          className={cx(
                            "navbar__mobile-link",
                            "navbar__mobile-link--toggle",
                            active && "navbar__mobile-link--active"
                          )}
                          onClick={() => toggleMobileSubmenu(item.label)}
                        >
                          <span>{item.label}</span>
                          <ChevronDown
                            size={16}
                            className={cx("navbar__chevron", isExpanded && "navbar__chevron--rotated")}
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              className="navbar__mobile-sublist"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                            >
                              {item.dropdown.map((subItem) => {
                                const isSubActive = isPathActive(subItem.href);
                                return (
                                  <a
                                    key={subItem.label}
                                    href={subItem.href}
                                    className={cx(
                                      "navbar__mobile-subitem",
                                      isSubActive && "navbar__mobile-subitem--active"
                                    )}
                                    onClick={handleNavClick(subItem.href)}
                                  >
                                    {subItem.label}
                                  </a>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <a
                        href={item.href}
                        className={cx("navbar__mobile-link", active && "navbar__mobile-link--active")}
                        onClick={handleNavClick(item.href)}
                      >
                        {item.label}
                      </a>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Mobile Drawer Language Switcher */}
            <div className="navbar__mobile-lang">
              <LanguageMenu
                currentLang={currentLang}
                onSelectLanguage={handleLanguageChange}
                isMobile
              />
            </div>

            <div className="navbar__mobile-action">
              <Button block icon={<ArrowRight size={16} />} onClick={handleNavClick("/contact")}>
                Get Started
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Navbar;